import { z } from "zod";
/**
 * What this person can actually train with — `GET`/`PUT /me/equipment`
 * (`D115`).
 *
 * ── THE PUT IS THE WHOLE LIST, ALWAYS ───────────────────────────────────────
 * The equipment screen is a checklist: the person unticks *Barbell* as often
 * as they tick it, and a delta API ("add these, remove those") makes the
 * SCREEN responsible for computing a diff against a state it may have fetched
 * minutes ago. Replacing the list makes the last write win, which is what a
 * checklist means.
 */
/**
 * The four groups the equipment screen sorts its checklist into.
 *
 * There is NO COLUMN for this (`D115`): the catalogue's 31 rows carry a name
 * and nothing else, and grouping is a product decision about how to lay out a
 * screen, not a fact about the equipment. So it is a frozen constant here,
 * where the app and the engine both read it.
 */
export const EQUIPMENT_GROUP_NAMES = [
    "free_weights",
    "machines",
    "accessories",
    "body_weight",
];
export const equipmentGroup = z.enum(EQUIPMENT_GROUP_NAMES);
/**
 * Every `catalog_equipment` id, 1–31 in seed order
 * (`dist/catalogue-seed/001.sql`), to its group.
 *
 * ── IDS AND NOT NAMES, AND THE NAMES STILL COME FROM THE DATABASE ───────────
 * The comment beside each id is a reading aid. The label the person sees comes
 * from `/catalog/filters`, localized (`D26`) — never from this file and never
 * from i18n, because the catalogue owns those names and translates them.
 *
 * ── IT IS TOTAL, AND A TEST SAYS SO ─────────────────────────────────────────
 * `__tests__/equipment.test.ts` asserts every id 1–31 appears exactly once. A
 * missing id would be an equipment row the checklist silently never shows, and
 * therefore a thing the person owns that the engine believes they do not.
 */
export const EQUIPMENT_GROUPS = Object.freeze({
    1: "accessories", // Ab Wheel Roller
    2: "accessories", // Band
    3: "free_weights", // Barbell
    4: "body_weight", // Body weight
    5: "accessories", // Bosu ball
    6: "machines", // Cable
    7: "free_weights", // Dumbbell
    8: "free_weights", // EZ Barbell
    9: "accessories", // Foam Roller
    10: "accessories", // Head Harness
    11: "free_weights", // Kettlebell
    12: "accessories", // Landmine
    13: "machines", // Leverage machine
    14: "accessories", // Medicine Ball
    15: "machines", // Power Sled
    16: "accessories", // Resistance Band
    17: "accessories", // Rollball
    18: "accessories", // Rope
    19: "machines", // Sled machine
    20: "machines", // Smith machine
    21: "free_weights", // Special Bar
    22: "accessories", // Special Bench
    23: "accessories", // Special Equipment
    24: "machines", // Special Machine
    25: "accessories", // Stability ball
    26: "accessories", // Stick
    27: "accessories", // Suspension
    28: "free_weights", // Trap bar
    29: "machines", // Treadmill
    30: "machines", // Vibrate Plate
    31: "free_weights", // Weighted
});
/** The number of `catalog_equipment` rows the seed loads. */
export const EQUIPMENT_ID_COUNT = 31;
const availableShape = {
    /**
     * `catalog_equipment` ids. Unique, unordered, and NOT validated against the
     * upper bound here: a seed that adds a 32nd row must not make every stored
     * list unparseable in an app built before it. An unknown id matches no
     * exercise, which is the correct behaviour for equipment the catalogue does
     * not have.
     */
    available: z
        .array(z.int().positive())
        .refine((ids) => new Set(ids).size === ids.length, {
        error: "available must not repeat an equipment id",
    }),
};
/**
 * Where the person trains (`D41`) — the vocabulary the onboarding script
 * offers for its `equipment_place` step, and the one the client's label table
 * already lists. Restated in `@repo/training`'s `EQUIPMENT_PLACES` (aggregate
 * purity) and in the CHECK on `athlete_equipment_place.place`.
 */
export const EQUIPMENT_PLACES = ["gym", "home", "outdoors", "mix"];
export const equipmentPlace = z.enum(EQUIPMENT_PLACES);
const placeShape = {
    /**
     * ── ABSENT IS NOT `null` ───────────────────────────────────────────────
     * On the `PUT`, an ABSENT `place` leaves the stored value alone — the
     * checklist screen and every client older than this field send only
     * `available`, and their save must not wipe it. `null` clears it. On the
     * way out it is always sent, `null` when never set.
     */
    place: equipmentPlace.nullable().optional(),
};
export const equipmentAvailabilityInput = z.strictObject({
    ...availableShape,
    ...placeShape,
});
/** The same shape back — the `PUT` echoes what is now stored. */
export const equipmentAvailabilityOutput = z.object({
    ...availableShape,
    ...placeShape,
});
//# sourceMappingURL=equipment.js.map